/* eslint-disable react/prop-types */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
  EditingState,
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  SearchState,
  IntegratedSelection,
  SelectionState,
  DataTypeProvider
} from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditRow,
  TableEditColumn,
  TableColumnReordering,
  TableColumnResizing,
  DragDropProvider,
  PagingPanel,
  ExportPanel,
  TableColumnVisibility,
  Toolbar,
  TableSelection,
  SearchPanel,
  ColumnChooser,
  TableInlineCellEditing
} from '@devexpress/dx-react-grid-material-ui';

import { useLocalStorage } from 'react-use';
import ImageFullScreenDialog from '../../../shared/dialog/ImageFullScreenDialog';

import { Button, Checkbox, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import ReactSelect from 'react-select';
import { format } from 'date-fns';
import employeeService from '../../../services/EmployeeService';
import employeeQualificationService from '../../../services/EmployeeQualificationService';

import HeaderFilterCell, {
  getRowCellValue,
  HeaderFilterContext
} from '../../CustomerNew/table/customTemplate/HeaderFilterCell';
import KebabCommandCell from './customTemplate/KebabCommandCell';
import { Popup, PopupEditing } from './QualificationPopupEditing';
import { CalendarProvider, QualificationFocusableCell } from './QualificationFocusableCell';
import {
  getMandatoryQualificationValue,
  getQualificationStatusColor,
  statusColorStyles,
  statusTextClasses,
  QUALIFICATION_INLINE_COLUMNS,
  validExpiryDate
} from '../helper/helper';
import { saveQualificationInlineChanges } from '../helper/saveQualificationInlineChanges';
import useQualificationUpload from '../helper/useQualificationUpload';
import toast from 'react-hot-toast';
import ResponsiveTableContainer, { StickyTableHead } from '../../common/ResponsiveTableContainer';

export { getQualificationStatusColor, statusColorStyles, statusTextClasses };

const ImageProvider = (props) => {
  return <DataTypeProvider formatterComponent={ImageFullScreenDialog} {...props} />;
};

const QualificationStatusProvider = (props) => {
  return <DataTypeProvider formatterComponent={QualificationStatusFormatter} {...props} />;
};

const QualificationStatusFormatter = ({ value, row }) => {
  const color = getQualificationStatusColor(value, row?.expiryDate, row?.issueDate, row?.referenceNumber);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span className={statusTextClasses[color] || 'tw-text-gray-700'} style={{ color: statusColorStyles[color] }}>
        {value}
      </span>
    </div>
  );
};

const MandatoryQualificationsProvider = (props) => {
  return (
    <DataTypeProvider
      formatterComponent={MandatoryQualificationsFormatter}
      editorComponent={MandatoryQualificationEditor}
      {...props}
    />
  );
};

const mandatoryQualificationOptions = [
  { label: 'True', value: true },
  { label: 'False', value: false }
];

const MandatoryQualificationEditor = ({ value, onValueChange, onBlur, autoFocus }) => (
  <Table.Cell>
    <ReactSelect
      aria-label="Mandatory qualification"
      name="employeeMandatoryQualifications"
      options={mandatoryQualificationOptions}
      value={mandatoryQualificationOptions.find((option) => option.value === value) || null}
      onChange={(option) => {
        if (!option) return;
        onValueChange(option.value);
        // ReactSelect does not consistently blur after selection. Commit after
        // its value change has reached EditingState so the backend save runs.
        // eslint-disable-next-line no-undef
        setTimeout(() => onBlur?.(), 0);
      }}
      onBlur={onBlur}
      autoFocus={autoFocus}
      isSearchable={false}
      isClearable={false}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  </Table.Cell>
);

const MandatoryQualificationsFormatter = ({ value }) => (
  <Checkbox
    checked={Boolean(value)}
    disabled
    inputProps={{ 'aria-label': `Mandatory qualification: ${value ? 'True' : 'False'}` }}
    style={{ color: value ? '#357a38' : undefined }}
  />
);

const ActiveStatusProvider = (props) => {
  return <DataTypeProvider formatterComponent={ActiveStatusFormatter} {...props} />;
};

const ActiveStatusFormatter = ({ value, row }) => {
  const isActive = value === 'Active';
  const color = row
    ? getQualificationStatusColor(row.status, row?.expiryDate, row?.issueDate, row?.referenceNumber)
    : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span
        style={{
          height: '8px',
          width: '8px',
          borderRadius: '50%',
          backgroundColor: isActive ? 'green' : 'red'
        }}
      ></span>
      <span className={statusTextClasses[color] || ''} style={{ color: statusColorStyles[color] || 'inherit' }}>
        {value}
      </span>
    </div>
  );
};

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

const QualificationToolbarContext = createContext({});
const useQualificationToolbarStyles = makeStyles((theme) => ({
  root: {
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    '& > div:first-of-type': { display: 'none' },
    '& #add-qualification-btn': { marginRight: theme.spacing(1) }
  }
}));

const QualificationToolbarRoot = (props) => {
  const { onAddQualification, isAddingQualification } = useContext(QualificationToolbarContext);
  const classes = useQualificationToolbarStyles();
  return (
    <Toolbar.Root {...props} className={classes.root}>
      <Button
        id="add-qualification-btn"
        variant="contained"
        color="primary"
        onClick={onAddQualification}
        disabled={isAddingQualification}
        startIcon={<AddIcon />}
        style={{ marginRight: 8 }}
      >
        Add Qualification
      </Button>
      {props.children}
    </Toolbar.Root>
  );
};

const qualificationEditorStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

const QualificationNameFormatter = ({ value }) => <>{value?.label || value || ''}</>;
const EmployeeEmailFormatter = ({ value, row }) => <>{row?.employeeDisplayName?.email || value || ''}</>;
const QUALIFICATION_ADD_COLUMNS = ['employeeDisplayName', 'name', 'expiryDate', 'issueDate', 'referenceNumber'];

const QualificationEditCell = ({
  employeeOptions,
  qualificationOptions,
  onEmployeeChange,
  onSelectImage,
  onRemoveImage,
  isConvertingImage,
  imageError,
  ...props
}) => {
  const { column, value, row, tableRow, onValueChange } = props;
  const today = format(new Date(), 'yyyy-MM-dd');
  let qualificationPlaceholder = 'Select employee first';
  if (row?.employeeDisplayName) {
    qualificationPlaceholder = qualificationOptions.length ? 'Select qualification' : 'No qualifications to submit';
  }

  if (tableRow?.type === TableEditRow.EDIT_ROW_TYPE) {
    return <TableEditRow.Cell {...props} editingEnabled={false} />;
  }

  if (column.name === 'employeeDisplayName') {
    return (
      <TableEditRow.Cell {...props} editingEnabled>
        <ReactSelect
          aria-label="Employee display name"
          placeholder="Select employee"
          options={employeeOptions}
          value={value || null}
          onChange={onEmployeeChange}
          isSearchable
          isClearable
          // eslint-disable-next-line no-undef
          menuPortalTarget={document.body}
          styles={qualificationEditorStyles}
        />
      </TableEditRow.Cell>
    );
  }

  if (column.name === 'name') {
    return (
      <TableEditRow.Cell {...props} editingEnabled>
        <ReactSelect
          aria-label="Qualification name"
          placeholder={qualificationPlaceholder}
          options={qualificationOptions}
          value={value || null}
          onChange={onValueChange}
          isDisabled={!row?.employeeDisplayName}
          isSearchable
          isClearable
          // eslint-disable-next-line no-undef
          menuPortalTarget={document.body}
          styles={qualificationEditorStyles}
        />
      </TableEditRow.Cell>
    );
  }

  if (column.name === 'issueDate' || column.name === 'expiryDate') {
    return (
      <TableEditRow.Cell {...props} editingEnabled>
        <TextField
          type="date"
          value={value || ''}
          onChange={(event) => onValueChange(event.target.value || null)}
          inputProps={{
            'aria-label': column.name === 'issueDate' ? 'Issue date' : 'Expiry date',
            max: column.name === 'issueDate' ? today : undefined,
            min: column.name === 'expiryDate' ? today : undefined
          }}
        />
      </TableEditRow.Cell>
    );
  }

  if (column.name === 'employeeEmail') {
    return <TableEditRow.Cell {...props} value={row?.employeeDisplayName?.email || ''} editingEnabled={false} />;
  }

  if (column.name === 'qualificationUrl') {
    return (
      <TableEditRow.Cell {...props} editingEnabled>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
          <Button component="label" color="primary" size="small" disabled={isConvertingImage}>
            {isConvertingImage ? 'Converting…' : value ? 'Replace image' : 'Upload image'}
            <input
              hidden
              type="file"
              accept="image/*,.heic,.heif,.pdf"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                onSelectImage(file);
              }}
            />
          </Button>
          {value && <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{value.name}</span>}
          {value && (
            <Button size="small" color="secondary" onClick={onRemoveImage}>
              Remove
            </Button>
          )}
          {imageError && <span style={{ color: '#dc2626', fontSize: 12 }}>{imageError}</span>}
        </div>
      </TableEditRow.Cell>
    );
  }

  if (!QUALIFICATION_ADD_COLUMNS.includes(column.name)) {
    return <TableEditRow.Cell {...props} value="" editingEnabled={false} />;
  }

  return <TableEditRow.Cell {...props} editingEnabled />;
};

const QualificationEditCommandCell = ({ children, addedRows, canSaveDraft, ...restProps }) => {
  const commands = React.Children.toArray(children).filter(
    (child) => !(addedRows.length && child?.props?.id === 'commit' && !canSaveDraft)
  );
  return <KebabCommandCell {...restProps}>{commands}</KebabCommandCell>;
};

const getRowId = (row) => row.id;

const getQualificationHeaderValue = (row, columnName) => {
  if (columnName === 'employeeStatus') {
    return row.employeeStatus ? 'Active' : 'Inactive';
  }

  if (columnName === 'employeeMandatoryQualifications') {
    return getMandatoryQualificationValue(row) ? 'True' : 'False';
  }

  return getRowCellValue(row, columnName);
};

// A single click is left to TableInlineCellEditing. Double-clicking a data row
// switches from inline editing to the full popup editor.
const ClickableQualificationRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      row._isDraft ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || row._isDraft || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  const statusColor = row
    ? getQualificationStatusColor(row.status, row.expiryDate, row.issueDate, row.referenceNumber)
    : null;
  const textColor = statusColorStyles[statusColor];
  const textClass = statusTextClasses[statusColor] || '';

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.employeeDisplayName || 'qualification'} details` : undefined}
      className={`${restProps.className || ''} ${textClass}`.trim()}
      style={{
        ...restProps.style,
        color: textColor || restProps.style?.color,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

const QualificationTable = ({
  rows,
  setEmployeesQualifications,
  notSubmittedQualifications = [],
  setNotSubmittedQualifications = () => {},
  searchValue,
  setSearchState
}) => {
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [addedRows, setAddedRows] = useState([]);
  const { selectFile, cancelConversion, isConverting, fileError } = useQualificationUpload();

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const employeesResponse = await employeeService.getActiveEmployees();
        setActiveEmployees(employeesResponse?.data?.data || []);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Could not load active employees');
      }
    };
    loadOptions();
  }, []);

  const employeeOptions = useMemo(
    () =>
      activeEmployees.map((employee) => ({
        value: employee,
        label: employee.DisplayName || employee.displayName || employee.name || '',
        email:
          employee.PrimaryEmailAddr?.Address || employee.EmailAddress || employee.emailAddress || employee.email || ''
      })),
    [activeEmployees]
  );
  const draftRow = addedRows[0] || {};
  const draftEmployee = draftRow.employeeDisplayName?.value;
  const qualificationOptions = useMemo(() => {
    if (!draftEmployee) return [];
    const employeeId = String(draftEmployee._id || draftEmployee.id || '');
    const employeeKeyPayId = String(draftEmployee.KeyPayEmployeeId || draftEmployee.employeeKeyPayId || '');
    const employeeEmail = String(
      draftEmployee.PrimaryEmailAddr?.Address ||
        draftEmployee.EmailAddress ||
        draftEmployee.emailAddress ||
        draftEmployee.email ||
        ''
    ).toLowerCase();
    // eslint-disable-next-line no-undef
    const seen = new Set();

    return notSubmittedQualifications
      .filter((qualification) => {
        const matchesEmployeeId = employeeId && String(qualification.employeeId || '') === employeeId;
        const matchesKeyPayId = employeeKeyPayId && String(qualification.employeeKeyPayId || '') === employeeKeyPayId;
        const matchesEmail = employeeEmail && String(qualification.employeeEmail || '').toLowerCase() === employeeEmail;
        return matchesEmployeeId || matchesKeyPayId || matchesEmail;
      })
      .filter((qualification) => {
        const key = String(qualification.qualificationId || qualification.name);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((qualification) => ({
        value: { ...qualification, id: qualification.qualificationId },
        label: qualification.name
      }));
  }, [draftEmployee, notSubmittedQualifications]);
  const draftQualification = draftRow.name?.value;
  const draftDateIsRequired = !['Superannuation', 'Long Service Leave', 'Redundancy'].includes(
    draftQualification?.name
  );
  const today = format(new Date(), 'yyyy-MM-dd');
  const draftDatesAreValid =
    (!draftRow.issueDate || draftRow.issueDate <= today) && (!draftRow.expiryDate || draftRow.expiryDate >= today);
  const canSaveDraft = Boolean(
    (draftEmployee?.KeyPayEmployeeId || draftEmployee?.employeeKeyPayId) &&
      draftQualification?.id &&
      draftRow.referenceNumber?.trim() &&
      draftDatesAreValid &&
      !isConverting &&
      (!draftDateIsRequired || draftRow.issueDate || draftRow.expiryDate)
  );
  const [columns] = useState([
    { name: 'employeeDisplayName', title: 'DisplayName' },
    { name: 'name', title: 'Name' },
    { name: 'expiryDate', title: 'Expiry Date' },
    { name: 'issueDate', title: 'Issue Date' },
    { name: 'referenceNumber', title: 'Reference Number' },
    { name: 'qualificationUrl', title: 'Image' },
    { name: 'employeeEmail', title: 'Email' },
    { name: 'status', title: 'Qualification Status' },
    {
      name: 'employeeStatus',
      title: 'Employee Status',
      getCellValue: (row) => (row?.employeeStatus ? 'Active' : 'Inactive')
    },
    {
      name: 'employeeMandatoryQualifications',
      title: 'Mandatory Qualifications',
      getCellValue: getMandatoryQualificationValue
    }
  ]);

  const [qualificationColumnWidths, setQualificationColumnWidths] = useState([
    { columnName: 'employeeDisplayName', width: 180 },
    { columnName: 'name', width: 180 },
    { columnName: 'expiryDate', width: 180 },
    { columnName: 'issueDate', width: 180 },
    { columnName: 'referenceNumber', width: 180 },
    { columnName: 'qualificationUrl', width: 180 },
    { columnName: 'employeeEmail', width: 180 },
    { columnName: 'status', width: 180 },
    { columnName: 'employeeStatus', width: 180 },
    { columnName: 'employeeMandatoryQualifications', width: 180 }
  ]);

  const [qualificationColumnOrder, setQualificationColumnOrder] = useLocalStorage('qualificationColumnOrder', [
    'employeeDisplayName',
    'name',
    'expiryDate',
    'issueDate',
    'referenceNumber',
    'qualificationUrl',
    'employeeEmail',
    'status',
    'employeeStatus',
    'employeeMandatoryQualifications'
  ]);

  const [hiddenColumnNames, setHiddenColumnNames] = useLocalStorage('qualificationHiddenColumns', [
    'employeeEmail',
    'qualificationUrl'
  ]);

  const [sorting, setSorting] = useState([{ columnName: 'employeeDisplayName', direction: 'asc' }]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useLocalStorage('qualificationRowsPerPage', 50);
  const [pageSizes] = useState([50, 100, 200]);
  // const [searchValue, setSearchState] = useState('');
  const [selection, setSelection] = useState([]);
  const [columnFilters, setColumnFilters] = useLocalStorage('qualificationColumnFilters', {});

  const [imageColumn] = useState(['qualificationUrl']);
  const [qualificationStatusColumn] = useState(['status']);
  const [activeUsersColumn] = useState(['employeeStatus']);
  const [mandatoryQualificationsColumn] = useState(['employeeMandatoryQualifications']);

  //to differentiate between popup and inline editing
  const [, setEditingMode] = useState(null);
  const editingModeRef = useRef(null);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  const [validationStatus, setValidationStatus] = useState({});

  const changeEditingMode = useCallback((mode) => {
    editingModeRef.current = mode;
    setEditingMode(mode);
  }, []);

  const openQualificationDetails = useCallback(
    (rowId) => {
      if (addedRows.length) return;
      setEditingCellIds([]);
      changeEditingMode('popup');
      setEditingRowIds([rowId]);
    },
    [addedRows.length, changeEditingMode]
  );

  const qualificationRowComponent = useCallback(
    (props) => <ClickableQualificationRow {...props} onOpen={openQualificationDetails} />,
    [openQualificationDetails]
  );

  const qualificationCellComponent = useCallback(
    (props) => (
      <QualificationFocusableCell
        {...props}
        validationStatus={validationStatus}
        setValidationStatus={setValidationStatus}
      />
    ),
    [validationStatus]
  );

  const changeDraftEmployee = useCallback((employeeDisplayName) => {
    setAddedRows((currentRows) =>
      currentRows.map((row, index) => (index === 0 ? { ...row, employeeDisplayName, name: null } : row))
    );
  }, []);

  const selectDraftImage = useCallback(
    (file) => {
      selectFile(file, (qualificationUrl) => {
        setAddedRows((currentRows) =>
          currentRows.map((row, index) => (index === 0 ? { ...row, qualificationUrl } : row))
        );
      });
    },
    [selectFile]
  );

  const removeDraftImage = useCallback(() => {
    cancelConversion();
    setAddedRows((currentRows) =>
      currentRows.map((row, index) => (index === 0 ? { ...row, qualificationUrl: null } : row))
    );
  }, [cancelConversion]);

  const qualificationEditCellComponent = useCallback(
    (props) => (
      <QualificationEditCell
        {...props}
        employeeOptions={employeeOptions}
        qualificationOptions={qualificationOptions}
        onEmployeeChange={changeDraftEmployee}
        onSelectImage={selectDraftImage}
        onRemoveImage={removeDraftImage}
        isConvertingImage={isConverting}
        imageError={fileError}
      />
    ),
    [
      changeDraftEmployee,
      employeeOptions,
      fileError,
      isConverting,
      qualificationOptions,
      removeDraftImage,
      selectDraftImage
    ]
  );

  const editingStateColumnExtensions = useMemo(
    () =>
      columns.map(({ name }) => ({
        columnName: name,
        editingEnabled: QUALIFICATION_INLINE_COLUMNS.includes(name)
      })),
    [columns]
  );

  const latestRowsRef = useRef(rows);
  latestRowsRef.current = rows;
  const saveQueueRef = useRef(Promise.resolve()); // eslint-disable-line no-undef

  const commitChanges = async ({ added, changed }) => {
    let changedRows;
    if (added) {
      const row = added[0] || {};
      const employeeOption = row.employeeDisplayName;
      const qualificationOption = row.name;
      const employee = employeeOption?.value;
      const qualification = qualificationOption?.value;
      const dateIsRequired = !['Superannuation', 'Long Service Leave', 'Redundancy'].includes(qualification?.name);
      if (
        !(employee?.KeyPayEmployeeId || employee?.employeeKeyPayId) ||
        !qualification?.id ||
        !row.referenceNumber?.trim() ||
        (dateIsRequired && !row.issueDate && !row.expiryDate)
      ) {
        toast.error('Select an employee and qualification, enter a reference number, and provide a required date');
        return;
      }
      if (row.expiryDate && !validExpiryDate(`${row.expiryDate}T23:59:59`)) {
        toast.error('Qualification is already expired');
        return;
      }
      const qualificationData = {
        employeeKeyPayId: employee.KeyPayEmployeeId || employee.employeeKeyPayId,
        qualificationId: qualification.id,
        name: qualification.name,
        expiryDate: row.expiryDate ? `${row.expiryDate}T00:00:00` : null,
        issueDate: row.issueDate ? `${row.issueDate}T00:00:00` : null,
        referenceNumber: row.referenceNumber.trim(),
        documentId: null,
        status: 'accepted'
      };
      if (typeof row.qualificationUrl === 'string' && row.qualificationUrl.trim()) {
        qualificationData.qualificationUrl = row.qualificationUrl;
      }
      // eslint-disable-next-line no-undef
      const formData = new FormData();
      // eslint-disable-next-line no-undef
      formData.append('userId', localStorage.getItem('userId'));
      formData.append('qualificationData', JSON.stringify(qualificationData));
      // eslint-disable-next-line no-undef
      if (row.qualificationUrl instanceof File) {
        formData.append('file', row.qualificationUrl);
      }
      try {
        const response = await employeeQualificationService.updateEmployeeQualificationInQboByAdmin(formData);
        const saved = response?.data?.data || {};
        const savedRow = {
          ...qualificationData,
          ...saved,
          id:
            saved.id ||
            saved._id ||
            `${qualificationData.employeeKeyPayId}-${qualificationData.qualificationId}-${Date.now()}`,
          employeeId: employee._id || employee.id,
          employeeDisplayName: employeeOption.label,
          employeeEmail: employeeOption.email,
          employeeStatus: employee.portalAccess ?? true,
          employeeMandatoryQualifications: employee.mandatoryQualifications || [],
          qualificationUrl: saved.qualificationUrl ?? row.qualificationUrl ?? ''
        };
        setEmployeesQualifications((current) => [
          ...current.filter(
            (item) =>
              !(
                String(item.employeeKeyPayId) === String(qualificationData.employeeKeyPayId) &&
                String(item.qualificationId) === String(qualificationData.qualificationId) &&
                item.status?.toLowerCase() === 'not submitted'
              )
          ),
          savedRow
        ]);
        setNotSubmittedQualifications((current) =>
          current.filter(
            (item) =>
              !(
                String(item.employeeKeyPayId) === String(qualificationData.employeeKeyPayId) &&
                String(item.qualificationId) === String(qualificationData.qualificationId)
              )
          )
        );
        cancelConversion();
        setAddedRows([]);
        toast.success("Successfully added employee's qualification");
      } catch (error) {
        setAddedRows([row]);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Could not add qualification'
        );
      }
      return;
    }
    // Popup saves have their own API lifecycle. Queue inline saves so a later
    // edit to the same record includes previously saved fields in its payload.
    if (changed && editingRowIds.length === 0) {
      for (const [rowId, rowChanges] of Object.entries(changed)) {
        for (const [columnName, value] of Object.entries(rowChanges || {})) {
          if (!QUALIFICATION_INLINE_COLUMNS.includes(columnName)) continue;
          saveQueueRef.current = saveQueueRef.current.then(async () => {
            try {
              const row = latestRowsRef.current.find((item) => String(item.id) === rowId);
              if (!row) throw new Error('Qualification row was not found');
              const currentValue = getRowCellValue(row, columnName);
              if (columnName !== 'employeeMandatoryQualifications' && currentValue === value) return;
              const saved = await saveQualificationInlineChanges(row, { [columnName]: value });
              const mergeSaved = (current) =>
                current.map((item) => {
                  const matches =
                    columnName === 'employeeMandatoryQualifications'
                      ? item.employeeId === row.employeeId
                      : String(item.id) === rowId;
                  return matches ? { ...item, ...saved } : item;
                });
              latestRowsRef.current = mergeSaved(latestRowsRef.current);
              setEmployeesQualifications(mergeSaved);
              setValidationStatus((current) => ({
                ...current,
                [rowId]: { ...current[rowId], [columnName]: undefined }
              }));
              toast.success('Successfully updated qualification');
            } catch (error) {
              const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Could not update qualification';
              setValidationStatus((current) => ({
                ...current,
                [rowId]: { ...current[rowId], [columnName]: { isValid: false, error: message } }
              }));
              toast.error(message);
            }
          });
        }
      }
    }
    if (changedRows !== undefined) {
      setEmployeesQualifications(changedRows);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      for (const [columnName, selectedValues] of Object.entries(columnFilters)) {
        if (selectedValues?.length > 0 && !selectedValues.includes(getQualificationHeaderValue(row, columnName))) {
          return false;
        }
      }

      return true;
    });
  }, [rows, columnFilters]);

  const headerFilterRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        employeeStatus: getQualificationHeaderValue(row, 'employeeStatus'),
        employeeMandatoryQualifications: getQualificationHeaderValue(row, 'employeeMandatoryQualifications')
      })),
    [rows]
  );

  const exporterRef = useRef();
  const startExport = useCallback(() => {
    exporterRef.current.exportGrid();
  }, [exporterRef]);

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>Total rows selected: {selection.length}</div>

      <QualificationToolbarContext.Provider
        value={{
          isAddingQualification: addedRows.length > 0,
          onAddQualification: () => {
            if (addedRows.length) return;
            cancelConversion();
            setEditingRowIds([]);
            setHiddenColumnNames((hiddenColumnNames || []).filter((columnName) => columnName !== 'qualificationUrl'));
            setAddedRows([
              {
                id: 'new-qualification-draft',
                _isDraft: true,
                employeeDisplayName: null,
                name: null,
                referenceNumber: '',
                issueDate: '',
                expiryDate: '',
                qualificationUrl: null
              }
            ]);
          }
        }}
      >
        <HeaderFilterContext.Provider
          value={{ rows: headerFilterRows, columnFilters, onFilterChange: setColumnFilters }}
        >
          <Paper>
            <Grid rows={filteredRows} columns={columns} getRowId={getRowId}>
              <CalendarProvider for={['expiryDate', 'issueDate']} />
              <DataTypeProvider for={['name']} formatterComponent={QualificationNameFormatter} />
              <DataTypeProvider for={['employeeEmail']} formatterComponent={EmployeeEmailFormatter} />
              <ImageProvider for={imageColumn} />
              <QualificationStatusProvider for={qualificationStatusColumn} />
              <ActiveStatusProvider for={activeUsersColumn} />
              <MandatoryQualificationsProvider for={mandatoryQualificationsColumn} />

              <EditingState
                editingCellIds={editingCellIds}
                onEditingCellIdsChange={(cellIds) => {
                  if (editingModeRef.current !== 'popup' || editingModeRef.current === null) {
                    const editableCellIds = cellIds.filter(({ columnName }) =>
                      QUALIFICATION_INLINE_COLUMNS.includes(columnName)
                    );
                    changeEditingMode(editableCellIds.length ? 'inline' : null);
                    setEditingCellIds(editableCellIds);
                  }
                }}
                editingRowIds={editingRowIds}
                onEditingRowIdsChange={(rowIds) => {
                  setEditingRowIds(rowIds);
                  if (rowIds.length) {
                    setEditingCellIds([]);
                    changeEditingMode('popup');
                  } else if (editingModeRef.current === 'popup') {
                    changeEditingMode(null);
                  }
                }}
                onCommitChanges={commitChanges}
                addedRows={addedRows}
                onAddedRowsChange={(newAddedRows) => {
                  setAddedRows(newAddedRows);
                  if (newAddedRows.length) {
                    setEditingCellIds([]);
                    changeEditingMode(null);
                  } else {
                    cancelConversion();
                  }
                }}
                columnExtensions={editingStateColumnExtensions}
              />

              <PagingState
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />

              <SortingState sorting={sorting} onSortingChange={setSorting} />
              <IntegratedSorting />

              <SearchState value={searchValue} onValueChange={setSearchState} />
              <SelectionState selection={selection} onSelectionChange={setSelection} />

              <IntegratedPaging />

              <IntegratedSelection />
              <DragDropProvider />

              <Table
                rowComponent={qualificationRowComponent}
                cellComponent={qualificationCellComponent}
                containerComponent={ResponsiveTableContainer}
                headComponent={StickyTableHead}
              />

              <TableColumnReordering order={qualificationColumnOrder} onOrderChange={setQualificationColumnOrder} />
              <TableColumnResizing
                columnWidths={qualificationColumnWidths}
                onColumnWidthsChange={setQualificationColumnWidths}
              />

              <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />

              <TableColumnVisibility
                hiddenColumnNames={hiddenColumnNames}
                onHiddenColumnNamesChange={setHiddenColumnNames}
              />
              <TableSelection showSelectAll />

              {/* Pass the custom root to the Toolbar to move toolbar on the left */}
              <Toolbar rootComponent={QualificationToolbarRoot} />

              <SearchPanel />
              <ColumnChooser />

              <TableEditRow cellComponent={qualificationEditCellComponent} />

              {/* Native grid edit commands, displayed through the shared KebabCommandCell. */}
              <TableEditColumn
                width={150}
                showEditCommand
                cellComponent={(props) => (
                  <QualificationEditCommandCell {...props} addedRows={addedRows} canSaveDraft={canSaveDraft} />
                )}
              />
              <TableInlineCellEditing startEditAction="click" selectTextOnEditStart={true} />

              <PopupEditing
                popupComponent={Popup}
                setEmployeesQualifications={setEmployeesQualifications}
                rows={rows}
              />

              <PagingPanel pageSizes={pageSizes} />
              <ExportPanel startExport={startExport} />
            </Grid>
            <GridExporter
              ref={exporterRef}
              rows={filteredRows}
              columns={columns}
              hiddenColumnNames={hiddenColumnNames}
              selection={selection}
              onSave={onSave}
              getRowId={getRowId}
            />
          </Paper>
        </HeaderFilterContext.Provider>
      </QualificationToolbarContext.Provider>
    </div>
  );
};

export default QualificationTable;
